import { differenceInMinutes, differenceInSeconds, format, subMinutes, isAfter } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@medflex/utils/Tracking';
import isPermanentSession from '@medflex/utils/isPermanentSession';
import { SESSION_TYPES } from '@medflex/utils/constants';
import { VideoCameraIcon } from '@heroicons/react/24/solid';
import isPastMeeting from '@medflex/utils/isPastMeeting';
import Button from '../Button';
import Link from '../Link';
import ChatMessageBubble from '../ChatMessageBubble/ChatMessageBubble';
import { IconInformationOutline } from '../Icons';
import CallAppointmentIcon from './CallAppointmentIcon';

const matchScheduledTimeFrame = ({
  scheduled,
  scheduledAt,
  lastTouchedAt,
  scheduledEndedAt,
  establishedAt,
}) => {
  const now = new Date();

  return (
    scheduled &&
    isAfter(now, subMinutes(new Date(scheduledAt), 15)) &&
    !isPastMeeting({
      scheduledAt,
      lastTouchedAt,
      scheduledEndedAt,
      establishedAt,
    })
  );
};

const SessionMeetingMessage = ({
  endedAt,
  lastTouchedAt,
  establishedAt,
  scheduledAt,
  scheduleCanceledAt,
  scheduledEndedAt,
  infoArticleUrl,
  onParticipate,
  id,
  title,
  description,
  now,
  type,
  joinable,
}) => {
  const { t } = useTranslation('components');
  const establishTime = format(establishedAt, 'HH:mm');
  const endTime = format(endedAt || lastTouchedAt, 'HH:mm');

  const minutes = differenceInMinutes(endedAt || lastTouchedAt, establishedAt);
  const seconds = differenceInSeconds(endedAt || lastTouchedAt, establishedAt) % 60;

  const ended = !!endedAt;
  const canceled = !!scheduleCanceledAt;
  const scheduled = !ended && !canceled;
  const [matchesScheduledTimeFrame, setMatchesScheduledTimeFrame] = useState(
    matchScheduledTimeFrame({
      scheduled,
      scheduledAt,
      lastTouchedAt,
      scheduledEndedAt,
      establishedAt,
    }),
  );
  const missed = scheduled && isAfter(now, new Date(scheduledAt)) && !matchesScheduledTimeFrame;

  const getDateString = date =>
    `${t(`SessionMeetingMessage.scheduled_call.weekDays.${new Date(date).getDay()}`)}, ${format(
      date,
      'DD.MM.YYYY',
    )}`;

  const trackInfoArticleClicked = event => {
    trackEvent(event, {
      action: 'ChatMessageBubble | Link',
      actionText: 'Tipps zur Vorbereitung | (audience)',
    });
  };

  useEffect(() => {
    let interval;
    if (!matchesScheduledTimeFrame) {
      interval = setInterval(() => {
        if (matchScheduledTimeFrame({ scheduled, scheduledAt, lastTouchedAt, scheduledEndedAt })) {
          clearInterval(interval);
          setMatchesScheduledTimeFrame(true);
        }
      }, 60000);
    }

    return () => clearInterval(interval);
  }, [matchesScheduledTimeFrame, lastTouchedAt, scheduled, scheduledAt, scheduledEndedAt]);

  return (
    <ChatMessageBubble className={scheduled || canceled ? 'rounded-lg shadow bg-blue-100' : ''}>
      {scheduled || canceled ? (
        <div className="flex justify-center text-base">
          <div className="flex flex-col mr-8 min-w-0">
            <h2 className="font-semibold min-w-0 truncate">
              {scheduled && !missed && t('SessionMeetingMessage.scheduled_call.title', { title })}
              {canceled && t('SessionMeetingMessage.scheduled_call.title_canceled')}
              {missed && t('SessionMeetingMessage.scheduled_call.title_missed')}
            </h2>
            <ul className="flex flex-col mb-4">
              {!(scheduled && !missed) && <li>{`"${title}"`}</li>}
              {!isPermanentSession({ scheduledAt, type }) ? (
                <li>
                  {t('SessionMeetingMessage.scheduled_call.text', {
                    allowHtml: true,
                    date: getDateString(scheduledAt),
                    time: `${format(scheduledAt, 'HH:mm')}${
                      scheduledEndedAt ? ` - ${format(new Date(scheduledEndedAt), 'HH:mm')}` : ''
                    }`,
                  })}
                </li>
              ) : (
                t('SessionMeetingMessage.permanentSession')
              )}
              {description && <li className="italic">{`"${description}"`}</li>}
            </ul>
            {!canceled && !missed && (
              <Button
                isDisabled={
                  !isPermanentSession({ scheduledAt, type }) &&
                  (!matchesScheduledTimeFrame || !joinable)
                }
                className="mb-4"
                onClick={() => onParticipate({ id })}
                style={{ maxWidth: '350px' }}
              >
                {t('SessionMeetingMessage.scheduled_call.button')}
              </Button>
            )}
            {!matchesScheduledTimeFrame && !missed && !canceled && (
              <div className="text-xs text-slate-500 mb-2">
                {t('SessionMeetingMessage.scheduled_call.disabled_info')}
              </div>
            )}
            {infoArticleUrl && !missed && scheduled && (
              <Link
                href={infoArticleUrl}
                onClick={trackInfoArticleClicked}
                target="_blank"
                className="items-center"
                rel="noreferrer"
              >
                <IconInformationOutline size="4" className="fill-current" />
                <div className="ml-2">
                  {t('SessionMeetingMessage.scheduled_call.link', {
                    allowHtml: true,
                    url: infoArticleUrl,
                  })}
                </div>
              </Link>
            )}
          </div>
          <div className="self-center">
            <CallAppointmentIcon canceled={canceled} missed={missed} />
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center italic">
          <VideoCameraIcon className="h-8 w-8 mr-4 text-slate-400 fill-current" />
          {ended && (
            <span>
              {t('SessionMeetingMessage.success', { start: establishTime, end: endTime, title })}{' '}
              {minutes > 0 && t('SessionMeetingMessage.minutes', { count: minutes })}{' '}
              {minutes > 0 && seconds > 0 && t('SessionMeetingMessage.and')}
              {seconds > 0 && t('SessionMeetingMessage.seconds', { count: seconds })}
            </span>
          )}
        </div>
      )}
    </ChatMessageBubble>
  );
};

SessionMeetingMessage.defaultProps = {
  onParticipate: () => {},
  id: null,
  now: new Date(),
  joinable: false,
};

SessionMeetingMessage.propTypes = {
  createdAt: PropTypes.string,
  endedAt: PropTypes.string,
  establishedAt: PropTypes.string,
  scheduledAt: PropTypes.string,
  lastTouchedAt: PropTypes.string,
  scheduleCanceledAt: PropTypes.string,
  description: PropTypes.string,
  onParticipate: PropTypes.func,
  scheduledEndedAt: PropTypes.string,
  infoArticleUrl: PropTypes.string,
  id: PropTypes.string,
  now: PropTypes.instanceOf(Date),
  title: PropTypes.string,
  type: PropTypes.oneOf(Object.values(SESSION_TYPES)),
  joinable: PropTypes.bool,
};

export default SessionMeetingMessage;
